import React, { useMemo, useState } from 'react'
import { Background, CFlex, CMargin, DText, Flex, Padding } from '../../../../styles/CommonStyles'
import Text from '../../../../core/utils/Text'
import CoinSelect from '../../../common/input/CoinSelect'
import { deepCopy, formatNumber, stringToNumber } from '../../../../core/utils/common'
import ActionButton from '../../../common/buttons/ActionButton'
import ModalLayout from '../../../layout/main/ModalLayout'
import { SOCKET_URL } from '../../../../core/constants/urls'
import Input from '../../../common/input/Input'
import styled from 'styled-components'
import { useQueryContext } from '../../../../core/contexts/query'
import { useSetSwapSettings } from '../../../../core/services/react-query/setting/swap'
import Select from '../../../common/dropdown/Select'

const initialEditableCoin = {
    feeFactor: '', feeMin: '',
    feeMax: '', minAmount: '',
    maxAmount: ''
}

const THIRD_PARTIES = ['internal', 'kucoin']

const SwapSettings = ({ swapSettings }) => {

    const [showError, setShowError] = useState(false)
    const { setToast } = useQueryContext()

    const [modal, setModal] = useState({ open: false, type: null })
    const openModal = (type) => setModal({ open: true, type })
    const closeModal = () => setModal({ open: false, type: null })

    const [editableCoin, setEditableCoin] = useState(initialEditableCoin)
    const [coin, setCoin] = useState(null)
    const [thirdParty, setThirdParty] = useState(null)

    const { mutate, isLoading } = useSetSwapSettings(closeModal)

    const coinAdded = useMemo(() => {
        setShowError(false)
        if (!!coin && swapSettings?.currencies?.length) {
            const findCoin = swapSettings.currencies.find(x => x.currency === coin.id)
            if (!!findCoin) {
                setEditableCoin(findCoin)
                return findCoin
            }
        }
        setEditableCoin(initialEditableCoin)
        return null
    }, [swapSettings, coin])

    const onSubmit = () => {
        if (
            !!editableCoin.feeFactor &&
            !!editableCoin.minAmount &&
            !!editableCoin.maxAmount
        ) {
            const newCoins = deepCopy(swapSettings?.currencies || [])

            const obj = {
                currency: coin.id,
                minAmount: stringToNumber(editableCoin.minAmount),
                maxAmount: stringToNumber(editableCoin.maxAmount),
                feeFactor: stringToNumber(editableCoin.feeFactor),
                feeMin: stringToNumber(editableCoin.feeMin),
                feeMax: stringToNumber(editableCoin.feeMax),
                breakOnError: true
            }

            if (modal.type === 'add') {
                newCoins.push(obj)
            } else {
                const findIndex = newCoins.findIndex(x => x.currency === obj.currency)
                if (findIndex > -1) {
                    newCoins[findIndex] = { ...obj }
                }
            }

            // ✅ Wrap in required format
            const payload = {
                thirdParty: thirdParty || 'internal', // fallback if null
                currencies: newCoins
            }

            mutate(payload)
        } else {
            setShowError(true)
            setToast({ message: 'fill-inputs-error', show: true, isError: true })
        }
    }



    return (
        <>
            <Padding padding={'30px 10px'}>
                <CFlex style={{ gap: 20 }}>
                    <DText primary>
                        <Text tid={'swap-setting-title'} />
                    </DText>
                    <CMargin margin={4} />
                    <CoinSelect
                        width={'300px'}
                        value={coin}
                        onChange={setCoin}
                    />

                    <CMargin margin={'4px'} />
                    <Select
                        options={THIRD_PARTIES}
                        value={thirdParty}
                        placeHolder={'select-swap-third-party'}
                        onValueChange={(idx) => setThirdParty(THIRD_PARTIES[idx])}
                        size={'small'}
                        width={'300px'}
                        height={'40px'}
                    />

                    {!!coinAdded ?
                        <>
                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'minAmountSwap'} />
                                    <span>{` (${coinAdded.currency.toUpperCase()})`}</span>
                                </DText>
                                <TSItem>
                                    {formatNumber(coinAdded.minAmount, { type: coinAdded.currency })}
                                </TSItem>
                            </CFlex>
                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'maxAmountSwap'} />
                                    <span>{` (${coinAdded.currency.toUpperCase()})`}</span>
                                </DText>
                                <TSItem>
                                    {formatNumber(coinAdded.maxAmount, { type: coinAdded.currency })}
                                </TSItem>
                            </CFlex>
                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'feeFactorSwap'} />
                                </DText>
                                <TSItem>
                                    {formatNumber(coinAdded.feeFactor, { type: coinAdded.currency })}
                                </TSItem>
                            </CFlex>
                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'feeMinSwap'} />
                                    <span>{` (${coinAdded.currency.toUpperCase()})`}</span>
                                </DText>
                                <TSItem>
                                    {formatNumber(coinAdded.feeMin, { type: coinAdded.currency })}
                                </TSItem>
                            </CFlex>
                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'feeMaxSwap'} />
                                    <span>{` (${coinAdded.currency.toUpperCase()})`}</span>
                                </DText>
                                <TSItem>
                                    {formatNumber(coinAdded.feeMax, { type: coinAdded.currency })}
                                </TSItem>
                            </CFlex>

                            <CMargin />
                            <ActionButton active width={'300px'} height={'40px'} onClick={() => openModal('edit')}>
                                <Text tid={'edit-swap-setting'} />
                            </ActionButton>
                        </>
                        :
                        <>
                            <DText main>
                                <Text tid={'swap-setting-not-available'} />
                            </DText>
                            <ActionButton active width={'300px'} height={'40px'} onClick={() => openModal('add')}>
                                <Text tid={'add-swap-setting'} />
                            </ActionButton>
                        </>
                    }
                </CFlex>
            </Padding>

            <ModalLayout
                isStatic
                open={modal.open}
                width={'480px'}
            >
                <CFlex style={{ gap: 12 }}>
                    <DText primary>
                        <Text tid={`swap-title-${modal.type}`} />
                    </DText>
                    <Flex>
                        <img
                            src={
                                (coinAdded?.currency === 'irt' || coin?.id === 'irt') ?
                                    require('../../../../assets/images/tooman.png') :
                                    SOCKET_URL + `assets/icon/${coinAdded?.currency || coin?.id}.png`
                            }
                            alt={' '}
                            width={'32px'}
                        />
                        <DText main style={{ margin: '0 8px' }}>
                            {coinAdded?.currency?.toUpperCase() || coin?.id?.toUpperCase()}
                        </DText>
                    </Flex>
                    <Background bg={'mainBg'}>
                        <Input
                            size={'small'}
                            value={editableCoin.minAmount}
                            onInputChange={(v) => setEditableCoin(state => ({ ...state, minAmount: v }))}
                            label={'minAmountSwap'}
                            error={showError ? 'min-amount-required' : ''}
                            normal
                            number
                        />
                        <Input
                            size={'small'}
                            value={editableCoin.maxAmount}
                            onInputChange={(v) => setEditableCoin(state => ({ ...state, maxAmount: v }))}
                            label={'maxAmountSwap'}
                            error={showError ? 'max-amount-required' : ''}
                            normal
                            number
                        />
                        <Input
                            size={'small'}
                            value={editableCoin.feeFactor}
                            onInputChange={(v) => setEditableCoin(state => ({ ...state, feeFactor: v }))}
                            label={'feeFactorSwap'}
                            error={showError ? 'fee-factor-required' : ''}
                            normal
                            code
                        />
                        <Input
                            size={'small'}
                            value={editableCoin.feeMin}
                            onInputChange={(v) => setEditableCoin(state => ({ ...state, feeMin: v }))}
                            label={'feeMinSwap'}
                            number
                        />
                        <Input
                            size={'small'}
                            value={editableCoin.feeMax}
                            onInputChange={(v) => setEditableCoin(state => ({ ...state, feeMax: v }))}
                            label={'feeMaxSwap'}
                            number
                        />
                    </Background>

                    <Flex style={{ gap: 20 }} fw>
                        <ActionButton loading={isLoading} active height={'40px'} width={'45%'} onClick={onSubmit}>
                            <Text tid={'submit'} />
                        </ActionButton>
                        <ActionButton loading={isLoading} height={'40px'} width={'45%'} onClick={closeModal}>
                            <Text tid={'cancel'} />
                        </ActionButton>
                    </Flex>
                </CFlex>
            </ModalLayout></>
    )
}
const TSItem = styled.div`
    color: ${props => props.theme.color};
    direction: ltr;
    border-radius: 6px;
    border: 1px solid ${props => props.theme.color}10;
    background-color: ${props => props.theme.secondaryBg};
    padding: 8px 16px;
    width: 300px;
    height: 40px;
`
export default SwapSettings